import React, {
  useCallback,
  useState,
} from 'react';
import printJS from 'print-js';
import dayjs from 'utils/dayjs';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import Dialog from 'pages/Dashboard/components/Dialog';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  usePrintPatientFinancialHistory,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';

export type FinancialHistoryDialogParams = {
  patientId: number;
};

type Props = FinancialHistoryDialogParams & {
  onClose: () => void;
};

function getDefaultDateRange(): DateRange {
  return {
    min: dayjs().startOf('year').toDate(),
    max: dayjs().endOf('day').toDate(),
  };
}

export default function FinancialHistoryDialog({
  patientId,
  onClose,
}: Props) {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  const {
    isPending: isPrinting,
    mutateAsync: printFinancialHistory,
  } = usePrintPatientFinancialHistory();

  const onPrint = useCallback(async () => {
    const response = await printFinancialHistory({
      patientId,
      dateRange: serializeDateRange(dateRange),
    });
    printJS({
      printable: response?.list?.[0]?.url?.value,
      type: 'pdf',
      showModal: true,
    });
    onClose?.();
  }, [patientId, dateRange, onClose, printFinancialHistory]);

  return (
    <Dialog
      open
      title="Print Financial History"
      onClose={onClose}
      onConfirm={onPrint}
      confirmText={isPrinting ? 'Printing...' : 'Print'}
      disableConfirmButton={isPrinting}
      showActions
      disableEscapeKeyDown
      disableBackdropClick
      className="populate-form"
    >
      <Wrapper
        id="financial-history-report-period"
        label="Report Period"
        required
        showErrorText={false}
      >
        <DateRangePicker
          value={dateRange}
          onChange={(nextDateRange) => {
            if (nextDateRange) {
              setDateRange(nextDateRange);
            }
          }}
          classes={{ root: 'w-full', rangePicker: '!w-full !max-w-none' }}
        />
      </Wrapper>
    </Dialog>
  );
}
